import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import moment from 'moment-timezone';

import { useMembersOptionsForSelect } from '@hooks/useMembersOptionsForSelect';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@hooks/useUrlFilterSync';
import { getInitialReportDateRangeFromSearch } from '@utils/reportFiltersUrl';
import { useMemberTimezone } from '@hooks/useMemberTimezone';
import { useWorkingSchedule } from '@hooks/useWorkingSchedule';
import { useEffectiveHolidays } from '@hooks/useEffectiveHolidays';
import alertsService from '@services/alerts';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { useFocusVsDistraction } from '@hooks/useFocusVsDistraction';
import { queryKeys } from '@config/queryKeys';
import { fetchConsolidatedDay } from '@services/teamConsolidated';
import {
  fetchMemberAccumulator,
  fetchMemberDayData,
  fetchMemberRecentDays,
  fetchMemberAttendanceMonth,
  normalizeMemberDay,
} from '@services/memberConsolidated';
import {
  buildUserOverallProductivity,
  buildUserKeyHighlights,
  buildUserProductivityOverTime,
  buildUserChartTicks,
  buildUserActivityBreakdown,
  buildUserTopApplications,
  buildUserAttendanceSummary,
  buildUserActivityHeatmap,
} from '../DashboardOverviewNew.helpers';

export function useDashboardData() {
  const team = useSelector((state) => state.team);
  // See useTeamDashboardData.js for why `activeTeam` is preferred over `active`.
  const teamId = team?.activeTeam?.activeTeamid || team?.active?.activeTeamid;
  const adminUID = useSelector((s) => s.auth?.profile?.id);
  const companyId = useSelector(
    (s) => s.auth.profile?.activeCompany?.companyId
  );

  // ── Member list ──────────────────────────────────────────────────────────
  const { membersOptions, isLoading: isLoadingMembers } =
    useMembersOptionsForSelect(teamId);
  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  // ── Filters ───────────────────────────────────────────────────────────────
  // URL-synced (not just read once): refresh, browser back/forward, and deep
  // links (e.g. Timeline's "View Day Details") via
  // ?member=<uid>&start=<date>&end=<date> — same query keys used by
  // Timeline/AppUsage/Screenshots — all stay in sync with this page's state.
  const location = useLocation();
  const history = useHistory();

  const [dateRange, setDateRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );
  const [selectedMember, setSelectedMember] = useState(null);

  useUrlDateRangeSync({ location, setDateRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: adminUID,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });

  const writeParams = useMemo(() => {
    const params = { start: dateRange.start, end: dateRange.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    return params;
  }, [dateRange.start, dateRange.end, selectedMember?.value]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && dateRange?.start && dateRange?.end),
  });

  // ── Raw Firestore data ────────────────────────────────────────────────────
  const [rawToday, setRawToday] = useState(null);
  const [rawYesterday, setRawYesterday] = useState(null);
  const [rawLast7, setRawLast7] = useState([]);
  const [rawTeamDay, setRawTeamDay] = useState(null);
  const [rawAlerts, setRawAlerts] = useState(null);
  const [rawAttendanceMonth, setRawAttendanceMonth] = useState(null);

  // ── Loading flags ─────────────────────────────────────────────────────────
  const [isFetchingStats, setIsFetchingStats] = useState(false);
  const [isFetchingAlerts, setIsFetchingAlerts] = useState(false);

  const { data: appNameMapping = {} } = useQuery({
    queryKey: queryKeys.applicationInfo.mapping,
    queryFn: fetchApplicationInfoMapping,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // ── Fetch callbacks ───────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!selectedMember?.value || !teamId) return;
    setRawToday(null);
    setRawYesterday(null);
    setRawLast7([]);
    setRawTeamDay(null);
    setRawAttendanceMonth(null);
    setIsFetchingStats(true);
    try {
      const selectedDate = dateRange.start;
      const prevDate = moment(selectedDate)
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
      const userId = selectedMember.value;
      const attendanceMonthId = moment(selectedDate)
        .format('MMMM-YYYY')
        .toLowerCase();

      const [accDoc, yesterdayDoc, recentDocs, teamDayDoc, attendanceMonthDoc] =
        await Promise.all([
          fetchMemberAccumulator(teamId, userId),
          fetchMemberDayData(teamId, userId, prevDate),
          fetchMemberRecentDays(teamId, userId, 7),
          fetchConsolidatedDay(teamId, selectedDate),
          fetchMemberAttendanceMonth(userId, attendanceMonthId),
        ]);

      // Today's live data lives in the accumulator's day section;
      // fall back to daysData for the selected date when the accumulator is absent.
      const accDaySection = accDoc?.day;
      const accNorm =
        accDaySection?.dateId === selectedDate
          ? normalizeMemberDay(accDaySection)
          : null;
      const selectedDayDoc = accNorm
        ? null
        : await fetchMemberDayData(teamId, userId, selectedDate);
      const todayNorm = accNorm ?? normalizeMemberDay(selectedDayDoc);

      setRawToday(todayNorm);
      setRawYesterday(normalizeMemberDay(yesterdayDoc));

      // Build last-7 for heatmap: merge daysData with today from accumulator
      const normalized = recentDocs.map(normalizeMemberDay).filter(Boolean);
      const hasToday = normalized.some((d) => d.dateId === selectedDate);
      setRawLast7(
        todayNorm && !hasToday ? [...normalized, todayNorm] : normalized
      );

      setRawTeamDay(teamDayDoc);
      setRawAttendanceMonth(attendanceMonthDoc);
    } catch (_) {
      // widgets fall back to empty state
    } finally {
      setIsFetchingStats(false);
    }
  }, [selectedMember, dateRange.start, teamId]);

  const fetchAlerts = useCallback(async () => {
    if (!selectedMember?.value || !adminUID) return;
    setRawAlerts(null);
    setIsFetchingAlerts(true);
    try {
      const { success, data } = await alertsService.getMemberAlertsForAdmin(
        adminUID,
        selectedMember.value,
        { limit: 5 }
      );
      if (success) setRawAlerts(data);
    } finally {
      setIsFetchingAlerts(false);
    }
  }, [selectedMember, adminUID]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // ── Derived display data ──────────────────────────────────────────────────
  const timezone = useMemberTimezone(selectedMember?.value);

  const overallProductivityData = useMemo(
    () => buildUserOverallProductivity(rawToday, rawYesterday, rawTeamDay),
    [rawToday, rawYesterday, rawTeamDay]
  );

  const { data: focusVsDistractionData } = useFocusVsDistraction(
    rawToday?.appUsage,
    rawToday?.totalSec,
    rawToday?.productiveSec,
    { companyId, teamId }
  );

  const keyHighlightsData = useMemo(
    () =>
      buildUserKeyHighlights(
        rawToday,
        rawYesterday,
        rawAlerts,
        focusVsDistractionData
      ),
    [rawToday, rawYesterday, rawAlerts, focusVsDistractionData]
  );

  const productivityOverTimeData = useMemo(
    () => buildUserProductivityOverTime(rawToday, rawTeamDay, timezone),
    [rawToday, rawTeamDay, timezone]
  );

  const chartTicks = useMemo(
    () => buildUserChartTicks(rawToday, timezone),
    [rawToday, timezone]
  );

  const activityBreakdownData = useMemo(
    () => buildUserActivityBreakdown(rawToday),
    [rawToday]
  );

  const topApplicationsData = useMemo(
    () => buildUserTopApplications(rawToday?.appUsage, appNameMapping),
    [rawToday, appNameMapping]
  );

  const recentAlertsData = useMemo(() => {
    if (!rawAlerts) return null;
    return rawAlerts.map((alert) => ({
      id: alert.id,
      iconType: alert.severity === 'low' ? 'bar-chart' : 'triangle',
      iconBg: alert.severity === 'high' ? '#FFF1F3' : '#FFF6ED',
      iconColor: alert.severity === 'high' ? '#ef4444' : '#f59e0b',
      title: alert.title || 'Alert',
      subtitle: alert.metadata?.userEmailId || '',
      time: alertsService.formatDate(alert.createdAt),
    }));
  }, [rawAlerts]);

  const activityHeatmapData = useMemo(
    () => buildUserActivityHeatmap(rawLast7, dateRange.start, timezone),
    [rawLast7, dateRange.start, timezone]
  );

  const { workingSchedule } = useWorkingSchedule();
  const { holidays } = useEffectiveHolidays(teamId);
  const attendanceSummaryData = useMemo(
    () =>
      buildUserAttendanceSummary(
        rawAttendanceMonth,
        dateRange,
        workingSchedule.days,
        holidays
      ),
    [rawAttendanceMonth, dateRange, workingSchedule.days, holidays]
  );

  return {
    // Filters
    dateRange,
    setDateRange,
    membersOptions,
    selectedMember,
    setSelectedMember,
    isLoadingMembers,
    // Loading flags
    isFetchingStats,
    isFetchingAlerts,
    // Display data
    overallProductivityData,
    keyHighlightsData,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    recentAlertsData,
    productivityOverTimeData,
    chartTicks,
    activityHeatmapData,
    attendanceSummaryData,
  };
}
